import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Save, Trash2, MessageSquare, FormInput, ChevronDown } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { QRCodeGenerator } from "@/components/qr-code-generator";
import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, where, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface FormField {
  id: string;
  type: "text" | "textarea" | "select" | "radio" | "checkbox";
  label: string;
  required: boolean;
  options?: string[];
}

interface Form {
  id: string;
  name: string;
  description: string;
  fields: FormField[];
  createdAt: Date;
  updatedAt: Date;
}

interface FormResponse {
  id: string;
  formId: string;
  responses: {
    [fieldId: string]: string | string[];
  };
  submittedAt: Date;
}

export interface CustomerReferralHandle {
  handleCreateForm: () => Promise<void>;
}

interface CustomerReferralProps {
  onTabChange?: (tab: string) => void;
}

export const CustomerReferral = forwardRef<CustomerReferralHandle, CustomerReferralProps>(({ onTabChange }, ref) => {
  const { currentUser } = useAuth();
  const [forms, setForms] = useState<Form[]>([]);
  const [activeForm, setActiveForm] = useState<Form | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [savedForm, setSavedForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("forms");
  const [formResponses, setFormResponses] = useState<{ [formId: string]: FormResponse[] }>({});

  // Update parent component when tab changes
  useEffect(() => {
    onTabChange?.(activeTab);
  }, [activeTab, onTabChange]);

  // Redirect if not admin
  React.useEffect(() => {
    if (currentUser?.role !== "admin") {
      toast({
        variant: "destructive",
        title: "Zugriff verweigert",
        description: "Sie haben keine Berechtigung für diese Funktion.",
      });
    }
  }, [currentUser]);

  // Load forms on component mount
  useEffect(() => {
    const loadForms = async () => {
      if (!currentUser) return;

      try {
        const formsQuery = query(
          collection(db, "forms"),
          where("createdBy", "==", currentUser.id)
        );
        const querySnapshot = await getDocs(formsQuery);
        
        const loadedForms = querySnapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id,
          createdAt: doc.data().createdAt.toDate(),
          updatedAt: doc.data().updatedAt.toDate(),
        })) as Form[];

        setForms(loadedForms);
      } catch (error) {
        console.error("Error loading forms:", error);
        toast({
          variant: "destructive",
          title: "Fehler",
          description: "Die Formulare konnten nicht geladen werden.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadForms();
  }, [currentUser]);

  const handleCreateForm = async () => {
    if (!currentUser) return;

    const newForm: Omit<Form, "id"> = {
      name: "Neues Formular",
      description: "",
      fields: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: currentUser.id,
    };

    try {
      const docRef = await addDoc(collection(db, "forms"), {
        ...newForm,
        createdAt: Timestamp.fromDate(newForm.createdAt),
        updatedAt: Timestamp.fromDate(newForm.updatedAt),
      });

      const createdForm: Form = {
        ...newForm,
        id: docRef.id,
      };

      setForms([...forms, createdForm]);
      setActiveForm(createdForm);
      setIsEditing(true);

      toast({
        title: "Formular erstellt",
        description: "Das neue Formular wurde erfolgreich erstellt.",
      });
    } catch (error) {
      console.error("Error creating form:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Das Formular konnte nicht erstellt werden.",
      });
    }
  };

  const handleSaveForm = async (form: Form) => {
    if (!currentUser) return;

    try {
      const formRef = doc(db, "forms", form.id);
      const updatedForm = {
        ...form,
        updatedAt: Timestamp.fromDate(new Date()),
      };

      await updateDoc(formRef, updatedForm);

      setForms(forms.map(f => f.id === form.id ? { ...form, updatedAt: new Date() } : f));
      setIsEditing(false);
      setSavedForm(form);
      setShowQRCode(true);

      toast({
        title: "Formular gespeichert",
        description: "Das Formular wurde erfolgreich gespeichert.",
      });
    } catch (error) {
      console.error("Error saving form:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Das Formular konnte nicht gespeichert werden.",
      });
    }
  };

  const handleDeleteForm = async (formId: string) => {
    try {
      await deleteDoc(doc(db, "forms", formId));
      
      setForms(forms.filter(f => f.id !== formId));
      if (activeForm?.id === formId) {
        setActiveForm(null);
      }

      toast({
        title: "Formular gelöscht",
        description: "Das Formular wurde erfolgreich gelöscht.",
      });
    } catch (error) {
      console.error("Error deleting form:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Das Formular konnte nicht gelöscht werden.",
      });
    }
  };

  const handleAddField = (formId: string) => {
    const newField: FormField = {
      id: crypto.randomUUID(),
      type: "text",
      label: "",
      required: false,
    };
    
    setForms(forms.map(form => {
      if (form.id === formId) {
        return {
          ...form,
          fields: [...form.fields, newField],
          updatedAt: new Date(),
        };
      }
      return form;
    }));

    if (activeForm && activeForm.id === formId) {
      setActiveForm({
        ...activeForm,
        fields: [...activeForm.fields, newField],
        updatedAt: new Date(),
      });
    }
  };

  const handleUpdateField = (formId: string, fieldId: string, updates: Partial<FormField>) => {
    setForms(forms.map(form => {
      if (form.id === formId) {
        return {
          ...form,
          fields: form.fields.map(field => 
            field.id === fieldId ? { ...field, ...updates } : field
          ),
          updatedAt: new Date(),
        };
      }
      return form;
    }));

    if (activeForm && activeForm.id === formId) {
      setActiveForm({
        ...activeForm,
        fields: activeForm.fields.map(field => 
          field.id === fieldId ? { ...field, ...updates } : field
        ),
        updatedAt: new Date(),
      });
    }
  };

  const handleDeleteField = (formId: string, fieldId: string) => {
    setForms(forms.map(form => {
      if (form.id === formId) {
        return {
          ...form,
          fields: form.fields.filter(field => field.id !== fieldId),
          updatedAt: new Date(),
        };
      }
      return form;
    }));

    if (activeForm && activeForm.id === formId) {
      setActiveForm({
        ...activeForm,
        fields: activeForm.fields.filter(field => field.id !== fieldId),
        updatedAt: new Date(),
      });
    }
  };

  const loadAllFormResponses = async () => {
    try {
      const responsesQuery = query(collection(db, "formResponses"));
      const querySnapshot = await getDocs(responsesQuery);
      
      const responses = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        submittedAt: doc.data().submittedAt.toDate(),
      })) as FormResponse[];

      // Group responses by formId
      const groupedResponses = responses.reduce((acc, response) => {
        if (!acc[response.formId]) {
          acc[response.formId] = [];
        }
        acc[response.formId].push(response);
        return acc;
      }, {} as { [formId: string]: FormResponse[] });

      setFormResponses(groupedResponses);
    } catch (error) {
      console.error("Error loading form responses:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Die Antworten konnten nicht geladen werden.",
      });
    }
  };

  // Load all responses when switching to responses tab
  useEffect(() => {
    if (activeTab === "responses") {
      loadAllFormResponses();
    }
  }, [activeTab]);

  // Expose handleCreateForm to parent component
  useImperativeHandle(ref, () => ({
    handleCreateForm
  }));

  if (currentUser?.role !== "admin") {
    return null;
  }

  return (
    <div className="space-y-6" data-component="customer-referral">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="forms" className="flex items-center gap-2">
            <FormInput className="h-4 w-4" />
            Formulare
          </TabsTrigger>
          <TabsTrigger value="responses" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Antworten
            {Object.values(formResponses).reduce((total, responses) => total + responses.length, 0) > 0 && (
              <span className="bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
                {Object.values(formResponses).reduce((total, responses) => total + responses.length, 0)}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="forms" className="space-y-4">
          {forms.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-6">
                  <p className="text-muted-foreground mb-4">Noch keine Formulare vorhanden</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCreateForm}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Erstes Formular erstellen
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {forms.map((form) => (
                <Card key={form.id} className="relative">
                  <CardHeader>
                    <CardTitle>{form.name}</CardTitle>
                    <CardDescription>{form.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-muted-foreground">
                          {form.fields.length} Felder
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setActiveForm(form);
                              setIsEditing(true);
                            }}
                          >
                            Bearbeiten
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteForm(form.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {isEditing && activeForm && (
            <Card className="mt-6">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div className="space-y-1">
                    <CardTitle>Formular bearbeiten</CardTitle>
                    <CardDescription>
                      Erstellen Sie Ihr Formular mit benutzerdefinierten Feldern.
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setIsEditing(false)}>
                      Abbrechen
                    </Button>
                    <Button onClick={() => handleSaveForm(activeForm)}>
                      <Save className="mr-2 h-4 w-4" />
                      Speichern
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Name</label>
                    <Input
                      value={activeForm.name}
                      onChange={(e) => {
                        setActiveForm({ ...activeForm, name: e.target.value });
                      }}
                      placeholder="Formularname"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Beschreibung</label>
                    <Input
                      value={activeForm.description}
                      onChange={(e) => {
                        setActiveForm({ ...activeForm, description: e.target.value });
                      }}
                      placeholder="Formularbeschreibung"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">Formularfelder</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddField(activeForm.id)}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Feld hinzufügen
                    </Button>
                  </div>

                  {activeForm.fields.length === 0 ? (
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center py-6">
                          <p className="text-muted-foreground mb-4">Noch keine Felder vorhanden</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddField(activeForm.id)}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Erstes Feld hinzufügen
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-4">
                      {activeForm.fields.map((field, index) => (
                        <Card key={field.id} className="relative">
                          <CardContent className="pt-6">
                            <div className="space-y-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <label className="text-sm font-medium">Feldtyp</label>
                                    <select
                                      className="w-full rounded-md border border-input bg-background px-3 py-2"
                                      value={field.type}
                                      onChange={(e) =>
                                        handleUpdateField(activeForm.id, field.id, {
                                          type: e.target.value as FormField["type"],
                                          options: e.target.value === "select" || e.target.value === "radio" || e.target.value === "checkbox" 
                                            ? field.options || [""] 
                                            : undefined
                                        })
                                      }
                                    >
                                      <option value="text">Textfeld (einzeilig)</option>
                                      <option value="textarea">Textfeld (mehrzeilig)</option>
                                      <option value="select">Auswahlmenü</option>
                                      <option value="radio">Radio Buttons</option>
                                      <option value="checkbox">Checkboxen</option>
                                    </select>
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-sm font-medium">Feldbezeichnung</label>
                                    <Input
                                      type="text"
                                      value={field.label}
                                      onChange={(e) => {
                                        e.preventDefault();
                                        handleUpdateField(activeForm.id, field.id, {
                                          label: e.target.value,
                                        });
                                      }}
                                      placeholder="z.B. Name, E-Mail, Telefonnummer"
                                      className="w-full"
                                    />
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive ml-2"
                                  onClick={() => handleDeleteField(activeForm.id, field.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>

                              <div className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={`required-${field.id}`}
                                  checked={field.required}
                                  onChange={(e) =>
                                    handleUpdateField(activeForm.id, field.id, {
                                      required: e.target.checked,
                                    })
                                  }
                                  className="h-4 w-4 rounded border-gray-300"
                                />
                                <label
                                  htmlFor={`required-${field.id}`}
                                  className="text-sm font-medium"
                                >
                                  Pflichtfeld
                                </label>
                              </div>

                              {(field.type === "select" ||
                                field.type === "radio" ||
                                field.type === "checkbox") && (
                                <div className="space-y-2">
                                  <label className="text-sm font-medium">
                                    Antwortmöglichkeiten
                                  </label>
                                  <div className="space-y-2">
                                    {(field.options || [""]).map((option, optionIndex) => (
                                      <div key={optionIndex} className="flex gap-2">
                                        <Input
                                          value={option}
                                          onChange={(e) => {
                                            const newOptions = [...(field.options || [])];
                                            newOptions[optionIndex] = e.target.value;
                                            handleUpdateField(activeForm.id, field.id, {
                                              options: newOptions,
                                            });
                                          }}
                                          placeholder={`Option ${optionIndex + 1}`}
                                        />
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => {
                                            const newOptions = [...(field.options || [])];
                                            newOptions.splice(optionIndex, 1);
                                            handleUpdateField(activeForm.id, field.id, {
                                              options: newOptions.length ? newOptions : [""],
                                            });
                                          }}
                                          disabled={(field.options || []).length <= 1}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    ))}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        handleUpdateField(activeForm.id, field.id, {
                                          options: [...(field.options || []), ""],
                                        });
                                      }}
                                    >
                                      <Plus className="mr-2 h-4 w-4" />
                                      Option hinzufügen
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="responses" className="space-y-4">
          {Object.entries(formResponses).length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-6">
                  <p className="text-muted-foreground">Noch keine Antworten vorhanden</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Accordion type="multiple" className="space-y-4">
              {forms.map((form) => {
                const responses = formResponses[form.id] || [];
                if (responses.length === 0) return null;

                // Get all unique field IDs from all responses
                const allFieldIds = new Set<string>();
                responses.forEach(response => {
                  Object.keys(response.responses).forEach(fieldId => allFieldIds.add(fieldId));
                });

                // Create a map of field IDs to labels
                const fieldLabels = new Map(
                  form.fields.map(field => [field.id, field.label])
                );

                return (
                  <AccordionItem key={form.id} value={form.id}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{form.name}</span>
                        <span className="text-sm text-muted-foreground">
                          ({responses.length} {responses.length === 1 ? 'Antwort' : 'Antworten'})
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[200px]">Eingereicht am</TableHead>
                              {Array.from(allFieldIds).map(fieldId => (
                                <TableHead key={fieldId}>
                                  {fieldLabels.get(fieldId) || fieldId}
                                </TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {responses.map((response) => (
                              <TableRow key={response.id}>
                                <TableCell className="font-medium">
                                  {format(response.submittedAt, "dd.MM.yyyy HH:mm", { locale: de })}
                                </TableCell>
                                {Array.from(allFieldIds).map(fieldId => (
                                  <TableCell key={fieldId}>
                                    {Array.isArray(response.responses[fieldId])
                                      ? (response.responses[fieldId] as string[]).join(", ")
                                      : response.responses[fieldId] || "-"}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </TabsContent>
      </Tabs>

      {showQRCode && savedForm && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50">
          <div className="container mx-auto py-6 max-w-2xl">
            <div className="flex justify-end mb-4">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowQRCode(false);
                  setSavedForm(null);
                }}
              >
                Schließen
              </Button>
            </div>
            <QRCodeGenerator formId={savedForm.id} formName={savedForm.name} />
          </div>
        </div>
      )}
    </div>
  );
}); 
